import React, { useCallback, useEffect, useState } from 'react';
import Accounting from '../../../../component/accounting';
import AppLayout from '../../../../component/appLayout';
import { Group, Title } from './styles';
import {
  createNewLabel,
  deletelabel,
  getCurrentProjectDetails,
  updateProjectData
} from '../../../../services/projectServices';
import { useParams } from 'react-router-dom';
import { getTaskGroupList } from '../../../../services/taskServices';
import { isEmpty } from '../../../../helpers/common';
import { USER_ROLE } from '../../../../global/constants';
import { trackAnalyticActivity } from '../../../../services/analyticsService';
import { getCustomerMembersList } from '../../../../services/customerServices';
import { MemberInputInterface } from '../../../../interfaces/CustomerInterface';
import { captureException } from '../../../../services/logService';
import SettingCards from '../../../../component/projectSetting';
import { PROJECT_ANALYTICS } from '../../../../global/analyticsConstants';
import { rootStore } from '../../../../mobx/rootStore';
import { UserModel } from '../../../../mobx/models/user';
import { Label } from '../../../../mobx/interfaces/project';
import BottomSubNavigation from '../../../../component/bottomNavigation/subNavigation';
import { observer } from 'mobx-react-lite';

const Setting: React.FC = () => {
  // States
  const [loading, setLoading] = useState(false);
  const [customersUsers, setCustomersUsers] = useState<UserModel[]>([]);
  const [loadingButton, setLoadingButton] = useState({
    group: false,
    status: false,
    priority: false,
    label: false,
    multiAssignee: false,
    estimation: false,
    milestone: false
  });

  //Mobx store variables
  const { workspaceStore, projectStore, appStore } = rootStore;
  const { currentWorkspace } = workspaceStore;
  const { currentProject } = projectStore;
  const { themeMode } = appStore || {};
  const workspace_id = currentWorkspace?.id;

  // Other variables
  const params: { id: string } = useParams();

  const setSpecificLoading = (key: string, value: boolean) => {
    setLoadingButton((prevState) => ({
      ...(prevState || {}),
      [key]: value
    }));
  };

  // load initial data
  const loadData = useCallback(async () => {
    try {
      if (!isEmpty(workspace_id)) {
        setLoading(true);
        let result = await Promise.all([getCurrentProjectDetails(params?.id), getTaskGroupList(params?.id)]);
        let customerMembers: MemberInputInterface[] = [];
        if (result[0]?.customerId && !isEmpty(result[0]?.customerId)) {
          customerMembers = await getCustomerMembersList(result[0]?.customerId);
        }
        const filteredCustomers: UserModel[] =
          customerMembers?.length > 0 && currentWorkspace?.users && currentWorkspace?.users?.length > 0
            ? currentWorkspace?.users?.filter(
                (item) => item?.role === USER_ROLE.CLIENT && customerMembers?.some((a) => a?.id === item?.id)
              )
            : [];
        setCustomersUsers(filteredCustomers);
      }
    } catch (e) {
      captureException(e);
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.users, params?.id, workspace_id]);

  useEffect(() => {
    trackAnalyticActivity(PROJECT_ANALYTICS.VIEW_SETTING);
    loadData();
  }, [loadData]);

  // for update priority
  const onUpdatePriority = useCallback(
    async (value: boolean) => {
      try {
        setSpecificLoading('priority', true);
        const payload = {
          Priority: {
            Enabled: value,
            Default: Number(currentProject?.priority?.default) || 0
          }
        };
        const response = await updateProjectData(currentProject?.id, payload);
        if (response) {
          currentProject?.setPriorityEnabled(value);
          if (value) trackAnalyticActivity(PROJECT_ANALYTICS.PRIORITY_ENABLED);
          else trackAnalyticActivity(PROJECT_ANALYTICS.PRIORITY_DISABLED);
        }
      } catch (e) {
        captureException(e);
      } finally {
        setSpecificLoading('priority', false);
      }
    },
    [currentProject]
  );

  // for update labels
  const onUpdateLabels = useCallback(
    async (value: boolean) => {
      try {
        setSpecificLoading('label', true);
        const payload = {
          LabelsEnabled: value
        };
        if ((!currentProject?.labelsList || currentProject?.labelsList?.length === 0) && value) {
          const payloadUI = {
            name: 'UI',
            color: 'Purple'
          };
          const payloadBUG = {
            name: 'BUG',
            color: 'Red'
          };
          await Promise.all([
            createNewLabel(payloadUI, currentProject?.id),
            createNewLabel(payloadBUG, currentProject?.id)
          ]);
        }
        const response = await updateProjectData(currentProject?.id, payload);
        if (response) {
          if (value) trackAnalyticActivity(PROJECT_ANALYTICS.LABEL_ENABLED);
          else trackAnalyticActivity(PROJECT_ANALYTICS.LABEL_DISABLED);
          currentProject?.setLabelsEnabled(value);
          await getCurrentProjectDetails(params?.id);
        }
      } catch (e) {
        captureException(e);
      } finally {
        setSpecificLoading('label', false);
      }
    },
    [currentProject, params?.id]
  );

  // for delete label
  const deleteLabel = useCallback(
    async (labelId: string) => {
      try {
        setLoading(true);
        const result = await deletelabel(labelId, currentProject?.id);
        if (result) {
          const updateLabel = currentProject?.labelsList?.filter((item: Label) => item?.id !== labelId) || [];
          currentProject?.setLabel(updateLabel);
        }
      } catch (error) {
        captureException(error);
      } finally {
        setLoading(false);
      }
    },
    [currentProject]
  );

  // for update estimate functionality
  const onUpdateEstimate = useCallback(
    async (value: boolean) => {
      try {
        setSpecificLoading('estimation', true);
        const payload = {
          IsEstimate: value
        };
        const response = await updateProjectData(currentProject?.id, payload);
        if (response) {
          if (value) trackAnalyticActivity(PROJECT_ANALYTICS.ESTIMATION_ENABLED);
          else trackAnalyticActivity(PROJECT_ANALYTICS.ESTIMATION_DISABLED);
          currentProject?.setEstimate(value);
        }
      } catch (e) {
        captureException(e);
      } finally {
        setSpecificLoading('estimation', false);
      }
    },
    [currentProject]
  );

  // for update mile stone
  const onUpdateMileStone = useCallback(
    async (value: boolean) => {
      try {
        setSpecificLoading('milestone', true);
        let response;
        const payload = {
          IsMilestone: value
        };
        response = await updateProjectData(currentProject?.id, payload);
        if (response) {
          if (value) trackAnalyticActivity(PROJECT_ANALYTICS.MILESTONE_ENABLED);
          else trackAnalyticActivity(PROJECT_ANALYTICS.MILESTONE_DISABLED);
          currentProject?.setMilestone(value);
        }
      } catch (e) {
        captureException(e);
      } finally {
        setSpecificLoading('milestone', false);
      }
    },
    [currentProject]
  );

  // for manage multi assignee
  const onUpdateMultiAssignee = useCallback(
    async (value: boolean) => {
      try {
        setSpecificLoading('multiAssignee', true);
        let response;
        const payload = {
          MultiAsignee: value
        };
        response = await updateProjectData(currentProject?.id, payload);
        if (response) {
          if (value) trackAnalyticActivity(PROJECT_ANALYTICS.MULTI_ASSIGNEE_ENABLED);
          else trackAnalyticActivity(PROJECT_ANALYTICS.MULTI_ASSIGNEE_DISABLED);
          currentProject?.setMultiAssignee(value);
        }
      } catch (e) {
        captureException(e);
      } finally {
        setSpecificLoading('multiAssignee', false);
      }
    },
    [currentProject]
  );

  // for update status
  const onUpdateStatus = useCallback(
    async (value: boolean) => {
      try {
        setSpecificLoading('status', true);
        let response;
        const payload = {
          StatusEnable: value
        };
        response = await updateProjectData(currentProject?.id, payload);
        if (response) {
          if (value) trackAnalyticActivity(PROJECT_ANALYTICS.STATUS_ENABLED);
          else trackAnalyticActivity(PROJECT_ANALYTICS.STATUS_DISABLED);
          currentProject?.setStatusEnabled(value);
          if (currentProject?.statusData?.length === 0) loadData();
        }
      } catch (e) {
        captureException(e);
      } finally {
        setSpecificLoading('status', false);
      }
    },
    [currentProject, loadData]
  );

  // for update group
  const onUpdateGroup = useCallback(
    async (value: boolean) => {
      try {
        setSpecificLoading('group', true);
        let response;
        const payload = {
          IsGroupEnabled: value
        };
        response = await updateProjectData(currentProject?.id, payload);
        if (response) {
          if (value) trackAnalyticActivity(PROJECT_ANALYTICS.GROUP_ENABLED);
          else trackAnalyticActivity(PROJECT_ANALYTICS.GROUP_DISABLED);
          currentProject?.setGroupEnabled(value);
        }
      } catch (e) {
        captureException(e);
      } finally {
        setSpecificLoading('group', false);
      }
    },
    [currentProject]
  );

  return (
    <>
      <AppLayout>
        <Accounting />
        <Group>
          <Title>Project Settings</Title>
          <SettingCards
            currentProject={currentProject!}
            themeMode={themeMode}
            onUpdateMileStone={(value) => onUpdateMileStone(value)}
            onUpdateEstimate={(value) => onUpdateEstimate(value)}
            onUpdateMultiAssignee={(value) => onUpdateMultiAssignee(value)}
            onUpdateStatus={(value) => onUpdateStatus(value)}
            onUpdatePriority={(value) => onUpdatePriority(value)}
            onUpdateLabels={(value) => onUpdateLabels(value)}
            onUpdateGroup={(value) => onUpdateGroup(value)}
            taskGroups={currentProject?.taskGroups || []}
            loading={loading}
            customersUsers={customersUsers}
            paramsId={params?.id}
            deleteLabel={(id) => deleteLabel(id)}
            loadData={loadData}
            setLoading={(value: boolean) => setLoading(value)}
            workspaceDetails={currentWorkspace!}
            loadingButton={loadingButton}
          />
        </Group>
      </AppLayout>
      <BottomSubNavigation />
    </>
  );
};

export default observer(Setting);
